import React from 'react';
import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const PSYCHIC_ITEMS = new Set([1, 2, 3, 4, 5, 6, 14]);
const SOMATIC_ITEMS = new Set([7, 8, 9, 10, 11, 12, 13]);

const getOptionFromQuestion = (question, response) => {
    if (!question) return null;
    const refId = Number(response?.scaleOptionId);
    return (question.questionOptionScores || []).find((opt) => {
        const optId = opt.scaleOptionId || opt.scaleOption?.id || opt.id;
        return Number(optId) === refId;
    });
};

const deriveScoreFromResponse = (response, question) => {
    const direct = toNumberOrNull(response?.score);
    if (direct != null) return direct;
    const numeric = toNumberOrNull(response?.numericValue);
    if (numeric != null) return numeric;
    const option = getOptionFromQuestion(question, response);
    if (option) {
        const optScore = toNumberOrNull(option?.score);
        if (optScore != null) return optScore;
    }
    return null;
};

const inferItemNumber = (question) => {
    const fromOrder = toNumberOrNull(question?.order);
    if (fromOrder != null) return fromOrder;
    const candidates = [
        question?.code,
        question?.questionCode,
        question?.shortCode,
        question?.shortName,
        question?.name,
    ];
    for (const value of candidates) {
        if (!value || typeof value !== 'string') continue;
        const match = value.match(/(\d+)/);
        if (match) {
            const num = Number(match[1]);
            if (!Number.isNaN(num)) return num;
        }
    }
    return null;
};

const categorizeItem = (itemNumber) => {
    if (itemNumber == null) return null;
    if (PSYCHIC_ITEMS.has(itemNumber)) return 'psychic';
    if (SOMATIC_ITEMS.has(itemNumber)) return 'somatic';
    return null;
};

const computeEAHScores = (register, scale) => {
    if (!register || !scale) return null;
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    if (!responses.length || !questions.length) return null;

    const questionById = new Map(questions.map((q) => [String(q.id), q]));
    const groupScores = { psychic: 0, somatic: 0 };
    const groupMax = { psychic: 0, somatic: 0 };
    let totalScore = 0;
    let totalMaxScore = 0;

    questions.forEach((question) => {
        const itemNumber = inferItemNumber(question);
        const bucket = categorizeItem(itemNumber);
        const options = question.questionOptionScores || [];
        const maxScore = options.length
            ? Math.max(...options.map((opt) => toNumberOrNull(opt?.score) ?? 0), 0)
            : 0;
        totalMaxScore += maxScore;
        if (bucket && Number.isFinite(maxScore)) {
            groupMax[bucket] += maxScore;
        }
    });

    responses.forEach((response) => {
        const question = questionById.get(String(response?.scaleQuestionId));
        if (!question) return;
        const itemNumber = inferItemNumber(question);
        const bucket = categorizeItem(itemNumber);
        const score = deriveScoreFromResponse(response, question);
        if (score == null) return;
        totalScore += score;
        if (bucket) {
            groupScores[bucket] += score;
        }
    });

    return {
        psychicScore: groupScores.psychic,
        somaticScore: groupScores.somatic,
        totalScore,
        psychicMax: groupMax.psychic || 28,
        somaticMax: groupMax.somatic || 28,
        totalMaxScore: totalMaxScore || 56,
    };
};

export const ScaleResultEAH = ({
    title = 'Resultados',
    psychicScore,
    somaticScore,
    maxSubScore = 28,
    totalScore,
    totalMaxScore = 56,
    note = 'Nota: Una mayor puntuación indica una mayor intensidad de la ansiedad.',
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(() => computeEAHScores(register, scale), [register, scale]);
    React.useEffect(() => {
        if (derived && typeof onComputedResult === 'function') {
            onComputedResult(derived);
        }
    }, [derived, onComputedResult]);
    const displayPsychic = toNumberOrNull(psychicScore) ?? derived?.psychicScore ?? 0;
    const displaySomatic = toNumberOrNull(somaticScore) ?? derived?.somaticScore ?? 0;
    const displayTotal = toNumberOrNull(totalScore) ?? derived?.totalScore ?? (displayPsychic + displaySomatic);
    const maxPsychic = toNumberOrNull(maxSubScore) ?? derived?.psychicMax ?? 28;
    const maxSomatic = toNumberOrNull(maxSubScore) ?? derived?.somaticMax ?? 28;
    const maxTotal = toNumberOrNull(totalMaxScore) ?? derived?.totalMaxScore ?? (maxPsychic + maxSomatic);

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <List style={{ paddingTop: 0, marginBottom: 16 }}>
                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a', fontWeight: 600 } }}
                        primary={`Ansiedad Psíquica: ${displayPsychic}/${maxPsychic}`}
                    />
                </ListItem>
                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a', fontWeight: 600 } }}
                        primary={`Ansiedad Somática: ${displaySomatic}/${maxSomatic}`}
                    />
                </ListItem>
                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a', fontWeight: 600 } }}
                        primary={`Puntuación Total: ${displayTotal}/${maxTotal}`}
                    />
                </ListItem>
            </List>

            {note && (
                <Typography variant="body2" style={{ fontStyle: 'italic', color: '#0d1b2a' }}>
                    {note}
                </Typography>
            )}
        </Paper>
    );
};

export default ScaleResultEAH;
